import { format, isValid, parse } from 'date-fns'
import InputTypes from '../constants/editabletypes.constants'
import { dateTimeConstants } from '../constants/datetime.constants'

const defaultDisplayDateFormat: string = dateTimeConstants.DEFAULT_DATE_FORMAT
const defaultDisplayTimeFormat: string = dateTimeConstants.DEFAULT_TIME_FORMAT

export const parseToUTCDate = (
    dateString: string,
    formatString: string
): Date => {
    try {
        const parsed = parse(dateString, formatString, new Date())
        if (!isValid(parsed)) {
            return new Date()
        }

        return new Date(
            Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
        )
    } catch (err) {
        return new Date()
    }
}

export const formatDate = (
    inputTypeId: number,
    date: Date | string | null | undefined,
    dateFormat: string | null = null
): string | null => {
    if (!date) return null

    const dt: Date = typeof date === 'string' ? new Date(date) : date

    if (!isValid(dt)) return null

    // fallback formats
    const formatStr: string =
        inputTypeId === InputTypes.TimePicker
            ? dateFormat || defaultDisplayTimeFormat
            : dateFormat || defaultDisplayDateFormat

    try {
        return format(dt, formatStr)
    } catch (err) {
        console.error('Date formatting error:', err)
        return null
    }
}

export interface TimeFormatShape {
    is24: boolean
    hasSeconds: boolean
    hasAmPm: boolean
    hourSep: string // literal(s) between hour and minute, e.g. ":"
    minuteSep: string // literal(s) between minute and second
    ampmSep: string // literal(s) before the am/pm marker, e.g. " "
}

// Derive the structural pieces of a date-fns time format.
export const parseTimeFormatShape = (formatString: string): TimeFormatShape => {
    const hasAmPm = /a/i.test(formatString)
    const hasSeconds = /s/.test(formatString)
    const is24 = /H/.test(formatString) && !/h/.test(formatString) && !hasAmPm
    const hourSepMatch = formatString.match(/[hH]+([^a-zA-Z]*)m+/)
    const minuteSepMatch = formatString.match(/m+([^a-zA-Z]*)s+/)
    const ampmSepMatch = formatString.match(/[ms]+([^a-zA-Z]*)a+/)
    return {
        is24,
        hasSeconds,
        hasAmPm,
        hourSep: hourSepMatch ? hourSepMatch[1] : ':',
        minuteSep: minuteSepMatch ? minuteSepMatch[1] : ':',
        ampmSep: ampmSepMatch ? ampmSepMatch[1] : ' ',
    }
}

// Reshape raw input into the target format as the user types. Idempotent, so it
// is safe to run on every keystroke. Callers should skip it while DELETING so
// backspace can remove auto-inserted separators.
export const autoFormatTime = (raw: string, shape: TimeFormatShape): string => {
    const { is24, hasSeconds, hasAmPm, hourSep, minuteSep, ampmSep } = shape
    const isDigit = (c: string): boolean => c >= '0' && c <= '9'
    const isLetter = (c: string): boolean => /[a-z]/i.test(c)
    const len = raw.length
    let i = 0

    // Skip a run of user-typed separators (anything that isn't a digit/letter).
    const skipSeparators = (): boolean => {
        let skipped = false
        while (i < len && !isDigit(raw[i]) && !isLetter(raw[i])) {
            i++
            skipped = true
        }
        return skipped
    }
    const readDigits = (max: number): string => {
        let out = ''
        while (i < len && isDigit(raw[i]) && out.length < max) {
            out += raw[i]
            i++
        }
        return out
    }

    // Ignore any leading separators the user typed (e.g. a stray space) so they
    // don't wipe the value by preventing the hour from ever being read.
    skipSeparators()

    // HOUR: 1 or 2 digits. A first digit that cannot begin a two-digit hour
    // (e.g. "9" in 12h, ">2" in 24h) ends the hour immediately (auto-advance);
    // a second digit that would push the hour out of range (>23/>12, or "00" in
    // 12h where the minimum is 1) is treated as the start of the minute instead.
    const maxHour = is24 ? 23 : 12
    const minHour = is24 ? 0 : 1
    let hour = ''
    while (i < len && isDigit(raw[i]) && hour.length < 2) {
        const candidate = hour + raw[i]
        if (candidate.length === 2) {
            const num = parseInt(candidate, 10)
            if (num > maxHour || num < minHour) break
        }
        hour += raw[i]
        i++
        if (hour.length === 1) {
            const first = parseInt(hour, 10)
            const canExtend = is24 ? first <= 2 : first <= 1
            if (!canExtend) break
        }
    }
    if (hour === '') return ''

    const sawSepAfterHour = skipSeparators()
    const minute = readDigits(2)

    // The hour is "complete" (so we show the separator) once it is two digits,
    // is a single digit that can't extend, the user typed a separator, or the
    // minute has started. Otherwise keep waiting for a possible second digit.
    const hourExtendable =
        hour.length === 1 &&
        (is24 ? parseInt(hour, 10) <= 2 : parseInt(hour, 10) <= 1)
    const hourComplete =
        hour.length === 2 ||
        !hourExtendable ||
        sawSepAfterHour ||
        minute.length > 0
    if (!hourComplete) return hour

    let out = hour + hourSep + minute

    if (hasSeconds) {
        const sawSepAfterMinute = skipSeparators()
        const second = readDigits(2)
        if (minute.length === 2 && (sawSepAfterMinute || second.length > 0)) {
            out += minuteSep + second
        }
    }

    if (hasAmPm) {
        skipSeparators()
        let ampm = ''
        while (i < len && isLetter(raw[i]) && ampm.length < 4) {
            ampm += raw[i]
            i++
        }
        if (ampm.length > 0) {
            out += ampmSep + ampm
        }
    }

    return out
}
